/**
 * Sprint UTENTI-1 — Pagina cambio password forzato (primo accesso o reset).
 *
 * Quando il backend espone `user.must_change_password = true`, l'AuthContext +
 * App.js dirottano automaticamente l'utente qui (route /change-password).
 * Finché l'utente non cambia la propria password, non può accedere al resto
 * dell'app.
 *
 * Questa stessa pagina è accessibile anche volontariamente dall'utente per
 * cambiare la propria password (link da impostazioni profilo, futuro).
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { KeyRound, LogOut } from "lucide-react";

export default function ChangePassword() {
  const { user, refreshUser, logout } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("Le due nuove password non coincidono"); return; }
    if (next.length < 10) { toast.error("La nuova password deve avere almeno 10 caratteri"); return; }
    setBusy(true);
    try {
      await api.patch("/users/me", {
        current_password: current,
        password: next,
      });
      toast.success("Password aggiornata");
      // Ricarica utente per sbloccare il redirect forzato
      await refreshUser?.();
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md p-6 shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
            <KeyRound className="h-5 w-5 text-amber-700" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">
              {forced ? "Cambio password obbligatorio" : "Cambia la tua password"}
            </h1>
            {forced && (
              <p className="text-xs text-slate-500">
                Per motivi di sicurezza devi cambiare la password al primo accesso.
              </p>
            )}
          </div>
        </div>
        <form className="space-y-3" onSubmit={submit}>
          <div>
            <Label className="text-xs">Password attuale</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                   required autoComplete="current-password"
                   data-testid="chpw-current" />
          </div>
          <div>
            <Label className="text-xs">Nuova password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                   required autoComplete="new-password" minLength={10}
                   placeholder="min 10, 1 maiuscola, 1 minuscola, 1 numero"
                   data-testid="chpw-new" />
          </div>
          <div>
            <Label className="text-xs">Conferma nuova password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   required autoComplete="new-password" minLength={10}
                   data-testid="chpw-confirm" />
          </div>
          <Button type="submit" disabled={busy} className="w-full"
                  data-testid="chpw-submit">
            {busy ? "Salvataggio…" : "Cambia password"}
          </Button>
        </form>
        {forced && (
          <Button type="button" variant="ghost" className="w-full mt-2 text-slate-500"
                  onClick={() => { logout?.(); nav("/login"); }}>
            <LogOut className="h-3.5 w-3.5 mr-1" />
            Esci e cambia account
          </Button>
        )}
      </Card>
    </div>
  );
}
